"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { AnimatePresence } from "framer-motion";
import { TenKGameProvider } from "@/components/dashboard/metrics-games/tenk-game-context";
import { OpportunityScoreStartupSelector } from "@/components/dashboard/metrics-games/opportunity-score-startup-selector";
import { TenKStartupSelector } from "@/components/dashboard/metrics-games/tenk-startup-selector";
import { MetricsGamesContent } from "@/components/dashboard/metrics-games-content";
import { MetricsGamesInline } from "@/components/landing/metrics-games-inline";

const FEATURES = [
  {
    step: "01",
    title: "Ranked Niche Opportunities",
    description:
      "Instead of browsing startup by startup, see which categories combine real revenue with lighter competition.",
    gameId: "game-3",
    gameLabel: "Try opportunity score"
  },
  {
    step: "02",
    title: "Verified Market Benchmarks",
    description:
      "Use TrustMRR-backed MRR, revenue, growth, and sale data to understand how strong a niche really is.",
    gameId: "game-2",
    gameLabel: "Try confidence"
  },
  {
    step: "03",
    title: "Startup-Level Drill-Down",
    description:
      "Open any niche and inspect the startups driving it, with links back to their TrustMRR pages.",
    gameId: "game-1",
    gameLabel: "Try niche builder"
  },
  {
    step: "04",
    title: "$10k MRR Planning",
    description:
      "Benchmark your traction against market data to estimate how long it could take to reach $10k MRR.",
    gameId: "game-6",
    gameLabel: "Try $10k estimator"
  }
];

export function LandingFeatures() {
  const [gamesOpen, setGamesOpen] = useState(false);
  const [gamesGameId, setGamesGameId] = useState("game-1");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const featuresRef = useRef<HTMLElement>(null);

  function openGames(gameId: string) {
    setGamesGameId(gameId);
    setGamesOpen(true);
  }

  function handleClose() {
    featuresRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setGamesOpen(false);
  }

  function toggleRow(step: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(step)) next.delete(step);
      else next.add(step);
      return next;
    });
  }

  return (
    <TenKGameProvider>
    <>
      <section ref={featuresRef} className="landing-features">
        <p className="landing-features-label">Why Use NicheMRR</p>
        <div className="features-grid-2x4">
          {FEATURES.map((feature, index) => {
            const isTenKRow = feature.gameId === "game-6";
            const isOpportunityScoreRow = feature.gameId === "game-3";
            const gameLeftLayout = isTenKRow || isOpportunityScoreRow;
            const baseWordsFirst = gameLeftLayout ? false : index % 2 === 0;
            const wordsFirst = index === 0 ? true : baseWordsFirst;
            const isExpanded = expandedRows.has(feature.step);
            const wordsCell = (
              <div
                key={`${feature.step}-words`}
                className={`features-cell features-cell--words features-cell--words-${wordsFirst ? "left" : "right"}`}
              >
                <span className="feature-step">{feature.step}</span>
                <div className="features-cell-content">
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-desc">{feature.description}</p>
                  {isTenKRow && (
                    <div className="features-cell-tenk-selector">
                      <TenKStartupSelector slot="selected" />
                    </div>
                  )}
                  {isOpportunityScoreRow && (
                    <div className="features-cell-tenk-selector">
                      <OpportunityScoreStartupSelector slot="selected" />
                    </div>
                  )}
                </div>
              </div>
            );
            const gameCell = (
              <div key={`${feature.step}-game`} className="features-cell features-cell--game">
                <MetricsGamesContent
                  activeGameId={feature.gameId}
                  compact
                  tenkSearchInCard={isTenKRow}
                  opportunityScoreSearchInCard={isOpportunityScoreRow}
                />
              </div>
            );
            return (
              <div
                key={feature.step}
                className={`features-row ${isExpanded ? "features-row--expanded" : "features-row--collapsed"}`}
              >
                {wordsFirst ? (
                  <>
                    {wordsCell}
                    {gameCell}
                  </>
                ) : (
                  <>
                    {gameCell}
                    {wordsCell}
                  </>
                )}
                <button
                  type="button"
                  className="features-row-expand"
                  onClick={() => toggleRow(feature.step)}
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? "Collapse" : "Expand"}
                >
                  <span className="features-row-expand-icon" aria-hidden>
                    {isExpanded ? "▲" : "▼"}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
        <div className="features-footer">
          <Link href="/dashboard/metrics" className="feature-game-link">
            Full playground
          </Link>
        </div>
      </section>

      <AnimatePresence>
        {gamesOpen && (
          <MetricsGamesInline
            key="metrics-inline"
            initialGameId={gamesGameId}
            onClose={handleClose}
          />
        )}
      </AnimatePresence>
    </>
    </TenKGameProvider>
  );
}
